import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const content = {
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Privacy",
    description: "How Operandi Studio handles contact, order, and account information.",
    sections: [
      ["Information we collect", "We collect information you submit through the contact form, order details needed to deliver a digital product, and owner account information used to protect the admin dashboard. Payment details are processed by PayPal and are not stored on this site."],
      ["How information is used", "Information is used to respond to enquiries, process and deliver orders, prevent unauthorized admin access, maintain order records, and improve the store."],
      ["Cookies", "The private owner login uses secure cookies to keep the admin session active. These cookies are not used to sell personal information."],
      ["Sharing", "Payment information is shared with PayPal as needed to complete checkout. We do not sell customer information. Service providers may process limited information to operate hosting, storage, and payment delivery."],
      ["Retention and choices", "Contact and order records are kept as long as needed for support, accounting, and legal purposes. You may contact the store to ask about correcting or deleting information, subject to records that must be retained."],
    ],
  },
  terms: {
    title: "Terms of Service",
    eyebrow: "Terms",
    description: "Terms for browsing and buying digital products from Operandi Studio.",
    sections: [
      ["Digital products", "Unless a product states otherwise, purchases are licensed for use by one business. Files may not be resold, redistributed, published, or presented as your own work."],
      ["Product information", "We aim to describe each product clearly, including its category, price, and delivered file. Product availability, descriptions, and prices may change before purchase."],
      ["Checkout and delivery", "Orders are processed through PayPal. A completed payment creates a digital delivery link. You are responsible for providing accurate contact information and keeping your download link private."],
      ["Acceptable use", "You agree not to misuse the website, interfere with checkout or admin systems, attempt unauthorized access, or use purchased files unlawfully."],
      ["No business outcome guarantee", "Digital tools can support planning and organization, but the store does not guarantee revenue, customer growth, financing, or other business results."],
    ],
  },
  refund: {
    title: "Refund Policy",
    eyebrow: "Refunds",
    description: "Refund guidance for digital product purchases from Operandi Studio.",
    sections: [
      ["Digital delivery", "Because digital files can be accessed immediately after payment, completed downloads are generally not refundable."],
      ["When a refund may be reviewed", "Refund requests may be reviewed for duplicate purchases, a payment completed without delivery access, a corrupted or inaccessible file, or a material mismatch between the product page and the delivered file."],
      ["How to request help", "Use the contact page as soon as possible and include the email used for purchase, product name, order details, and a clear description of the issue."],
      ["Response process", "The owner will review the order and delivery record, then reply through the contact details provided. Approved refunds are returned through the original PayPal payment where available."],
      ["Chargebacks", "Please contact the store before opening a payment dispute so access or delivery issues can be investigated directly."],
    ],
  },
};

export function LegalPage({ type }) {
  const page = content[type];
  return (
    <Layout>
      <Seo title={page.title} description={page.description} />
      <section className="texture-noise px-6 pb-24 pt-36 sm:pt-40">
        <div className="mx-auto max-w-4xl">
          <Reveal className="text-center">
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">{page.eyebrow}</p>
            <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-[-0.04em] sm:text-5xl">{page.title}</h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-[#525866]">{page.description}</p>
          </Reveal>
          <div data-testid={`${type}-policy-page`} className="mt-12 space-y-5">
            {page.sections.map(([title, text], index) => (
              <Reveal key={title} delay={index * 0.05} className="rounded-[2rem] border border-[#E6E2DA] bg-white p-7 shadow-sm">
                <h2 className="font-heading text-xl font-bold">{title}</h2>
                <p className="mt-3 text-sm leading-7 text-[#525866]">{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
